'use client';

import { App, ConfigProvider, Layout } from 'antd';
import enUS from 'antd/es/calendar/locale/en_US';
import classNames from 'classnames';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
// import MobileRemind from '@/components/MobileRemind';
import cookie from 'react-cookies';

import type { IHomeContext } from '@/context/HomeContext';
import HomeContext from '@/context/HomeContext';
import useLayoutSize, { LAYOUT_SIZE } from '@/hooks/useLayoutSize';
import { useModel } from '@/hooks/useModel';
import useStorage from '@/hooks/useStorage';
import useTheme from '@/hooks/useTheme';
import { useTranslation } from '@/i18n/client';
import useUserSettings from '@/store/useUserSettings';
import { EGlobalCookieKey } from '@/types/cookie';
import { themeConfig } from '@/utils/UI/ui';

import styles from './index.less';

const { Content } = Layout;

const Home = (props: { children: React.ReactNode }) => {
  const { children } = props;
  const [refreshNum, setRefreshNum] = useState(0);
  const contextRef = useRef<IHomeContext>({} as IHomeContext);

  const context: IHomeContext = useMemo(() => {
    const setContext = (_context: Partial<IHomeContext>) => {
      contextRef.current = {
        ...contextRef.current,
        ..._context
      };
      setRefreshNum(refreshNum + 1);
    };

    return {
      ...contextRef.current,
      setContext
    };
  }, [refreshNum]);

  return <HomeContext.Provider value={context}>{children}</HomeContext.Provider>;
};

function LayoutComponent({ children }: { children: React.ReactNode }): JSX.Element {
  const _layoutSize = useLayoutSize();
  const [layoutSize, setLayoutSize] = useStorage('layoutSize');
  const [inMobile, setInMobile] = useState(_layoutSize === LAYOUT_SIZE.SMALL);

  const [direction, setDirection] = useStorage('direction');

  const { i18n } = useTranslation();

  const { isLogin } = useModel('auth-model', (model) => ({
    isLogin: model.isLogin
  }));

  const userLanguage = useUserSettings((state) => state.userLanguage);
  const fetchUserLanguage = useUserSettings((state) => state.fetchUserLanguage);

  useEffect(() => {
    if (_layoutSize !== undefined) {
      setLayoutSize(_layoutSize);
      setInMobile(_layoutSize === LAYOUT_SIZE.SMALL);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_layoutSize]);

  useTheme(layoutSize === LAYOUT_SIZE.SMALL);

  useEffect(() => {
    if (isLogin) {
      fetchUserLanguage();
    }
  }, [isLogin]);

  useEffect(() => {
    const _userLanguage = userLanguage;

    if (_userLanguage) {
      cookie.save(EGlobalCookieKey.userLanguage, _userLanguage, { path: '/' });
      localStorage.setItem('i18nextLng', _userLanguage);
      i18n.changeLanguage(_userLanguage);

      if (userLanguage === 'ar') {
        document.body.dir = 'rtl';
        setDirection('rtl');
      } else {
        document.body.dir = 'ltr';
        setDirection('ltr');
      }
    }
  }, [userLanguage]);

  return (
    <ConfigProvider direction={direction} locale={enUS.lang} theme={themeConfig}>
      <Home>
        <App>
          <Layout key="layout" className={styles.largeContainer}>
            <Content
              key="content"
              className={classNames(styles.layoutContent, {
                [styles.inMobile]: inMobile
              })}
            >
              <div key="outlet" className={styles.outlet} id="outlet">
                {children}
              </div>
            </Content>
          </Layout>
        </App>
      </Home>
    </ConfigProvider>
  );
}

export default memo(LayoutComponent);
